import StatusChip from 'components/@extended/StatusChip';
import MatterIndicators, { hasMatterAccess } from 'components/matters/MatterIndicators';
import EntityLinkList from 'components/misc/EntityLinkList';
import LineClamp from 'components/misc/LineClamp';
import RelationshipPartnersAndManagers from 'components/misc/RelationshipPartnersAndManagers';
import SupervisedAndResponsibleEmployees from 'components/misc/SupervisedAndResponsibleEmployees';
import PartyName from 'components/party/PartyName';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Tooltip } from 'components/ui/tooltip';
import { formatRateRange } from 'pages/pricelists/rateRange';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ClassifierData } from 'types/classifiers';
import { MatterVisibility, NewCaseListItem, NewCasesGroupBy } from 'types/matters';
import { ReferralListItem, ReferralTypeCode } from 'types/referrals';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { ENTITY_LINK_CLASS, getStatusChipType } from '../util';

const NONE = '—';

const OTHER_GROUPS_TOOLTIP: Record<NewCasesGroupBy, string> = {
    [NewCasesGroupBy.PRACTICE_GROUP]: 'matters.new-cases.other-groups-tooltip-practice',
    [NewCasesGroupBy.SECTOR_GROUP]: 'matters.new-cases.other-groups-tooltip-sector',
};

function isLimited(row: NewCaseListItem): boolean {
    return row.visibility === MatterVisibility.LIMITED;
}

function Referral({
    referral,
    classifiers,
}: {
    referral: ReferralListItem;
    classifiers: Record<string, ClassifierData>;
}) {
    const { referralTypeCode, employee, personParty, companyParty, otherSourceTypeCode } = referral;
    if (referralTypeCode === ReferralTypeCode.EMPLOYEE) {
        return <EntityLinkList domain="employee" items={employee ? [employee] : []} newTab />;
    }
    if (referralTypeCode === ReferralTypeCode.OTHER) {
        return <span>{getClassifierText(classifiers['REFERRAL_OTHER_SOURCE_TYPE'], otherSourceTypeCode)}</span>;
    }
    // A contact person is followed by the company contact the referral links them to, if any.
    return (
        <div className="flex flex-col">
            {personParty?.id ? <PartyName party={personParty} isLink linkTarget="_blank" variant="list" /> : null}
            {companyParty?.id ? <PartyName party={companyParty} isLink linkTarget="_blank" variant="list" /> : null}
        </div>
    );
}

/** The NEW tab's fixed case columns: no sorting, resizing, reordering or hiding. */
export function newCasesColumns(
    classifiers: Record<string, ClassifierData>,
    groupBy: NewCasesGroupBy
): DataTableColumnDef<NewCaseListItem>[] {
    return [
        {
            header: t('matters.new-cases.columns.status'),
            id: 'status',
            enableSorting: false,
            meta: { widthWeight: 0.8, minPx: 130 },
            cell: ({ row }) => (
                <div className="flex flex-wrap items-center gap-1">
                    <MatterIndicators
                        isConfidential={row.original.isConfidential}
                        hasAccess={hasMatterAccess(row.original)}
                        variant="chip"
                    />
                    <StatusChip
                        type={getStatusChipType(row.original.matterStateTypeCode)}
                        label={getClassifierText(classifiers['MATTER_STATE_TYPE'], row.original.matterStateTypeCode)}
                    />
                </div>
            ),
        },
        {
            header: t('matters.new-cases.columns.case-client'),
            id: 'caseClient',
            enableSorting: false,
            meta: { widthWeight: 1.5, minPx: 240 },
            cell: ({ row }) => {
                const { id, fullName, clientParty, otherGroups } = row.original;
                return (
                    <div className="flex w-full min-w-0 flex-col">
                        <div className="flex min-w-0 items-center gap-2">
                            <Link
                                to={`/matter/${id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(ENTITY_LINK_CLASS, 'line-clamp-2 min-w-0 font-semibold')}
                            >
                                {fullName}
                            </Link>
                            {otherGroups.length > 0 ? (
                                <Tooltip content={t(OTHER_GROUPS_TOOLTIP[groupBy], { names: otherGroups.join(', ') })}>
                                    <span className="shrink-0">
                                        <StatusChip
                                            size="small"
                                            type="info"
                                            label={
                                                <FormattedMessage
                                                    id="matters.new-cases.other-groups"
                                                    values={{ count: otherGroups.length }}
                                                />
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            ) : null}
                        </div>
                        {clientParty?.id ? (
                            <PartyName
                                party={clientParty}
                                isLink
                                linkTarget="_blank"
                                variant="list"
                                showPartyNumber
                                className="whitespace-normal"
                            />
                        ) : null}
                    </div>
                );
            },
        },
        {
            header: t('matters.new-cases.columns.description'),
            id: 'description',
            enableSorting: false,
            meta: { widthWeight: 1.5, minPx: 200 },
            cell: ({ row }) =>
                isLimited(row.original) ? null : <LineClamp maxLineCount={2}>{row.original.description}</LineClamp>,
        },
        {
            header: t('matters.responsible-persons'),
            id: 'responsiblePersons',
            enableSorting: false,
            meta: { widthWeight: 1, minPx: 180 },
            cell: ({ row }) => (
                <SupervisedAndResponsibleEmployees
                    supervisedByEmployees={row.original.supervisedByEmployee ?? undefined}
                    responsibleEmployees={row.original.responsibleEmployee ?? undefined}
                    newTab
                />
            ),
        },
        {
            header: t('matters.referrals'),
            id: 'referrals',
            enableSorting: false,
            meta: { widthWeight: 1, minPx: 180 },
            cell: ({ row }) => {
                if (isLimited(row.original)) {
                    return null;
                }
                const referrals = row.original.referrals ?? [];
                return referrals.length === 0 ? (
                    NONE
                ) : (
                    <div className="flex flex-col gap-1">
                        {referrals.map((referral) => (
                            <Referral key={referral.id} referral={referral} classifiers={classifiers} />
                        ))}
                    </div>
                );
            },
        },
        {
            header: t('contacts.relationship-owners'),
            id: 'relationshipOwners',
            enableSorting: false,
            meta: { widthWeight: 1, minPx: 180 },
            cell: ({ row }) =>
                isLimited(row.original) ? null : (
                    <RelationshipPartnersAndManagers
                        relationshipPartners={row.original.relationshipPartners ?? []}
                        relationshipManagers={row.original.relationshipManagers ?? []}
                        newTab
                    />
                ),
        },
        {
            header: t('matters.pricelist'),
            id: 'priceList',
            enableSorting: false,
            meta: { widthWeight: 1, minPx: 170 },
            cell: ({ row }) => {
                const { minHourlyPrice, maxHourlyPrice, avgHourlyPrice, pricelistCurrencyTypeCode } = row.original;
                if (isLimited(row.original)) {
                    return null;
                }
                return (
                    formatRateRange(minHourlyPrice, maxHourlyPrice, avgHourlyPrice, pricelistCurrencyTypeCode) || NONE
                );
            },
        },
    ];
}
